"use client";

/**
 * Simplified Brand Provider using Zustand Store
 * 
 * Replaces the complex client-brand-provider with a simpler approach
 * that uses our Zustand store for state management and caching.
 */

import type { BrandConfig } from '@/lib/shared/brand-config';
import { initializeBrandStore, useBrandStore } from '@/stores/brand-store';
import React, { createContext, useCallback, useContext, useEffect } from 'react';

interface ThemeColors {
  cssVariables: {
    light: Record<string, string>;
    dark: Record<string, string>;
  };
}

interface BrandContextType {
  brandConfig: BrandConfig;
  isLoading: boolean;
  error: string | null;
  refreshBrandConfig: () => Promise<void>;
  applyThemeColors: (colors: ThemeColors) => void;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

interface BrandProviderProps {
  children: React.ReactNode;
  initialBrandConfig?: BrandConfig;
}

export function BrandProvider({ children, initialBrandConfig }: BrandProviderProps) {
  const { 
    brandConfig, 
    isLoading, 
    error, 
    loadBrandConfig, 
    refreshBrandConfig,
    setBrandConfig 
  } = useBrandStore();

  // Initialize store with server-provided config if available
  useEffect(() => {
    if (initialBrandConfig) {
      setBrandConfig(initialBrandConfig);
    } else {
      // Initialize from localStorage if no server config
      initializeBrandStore();
    }
  }, [initialBrandConfig, setBrandConfig]);

  // Load brand config on mount if needed
  useEffect(() => {
    loadBrandConfig();
  }, [loadBrandConfig]);

  // Apply theme CSS when brand config changes
  useEffect(() => {
    const root = document.documentElement;

    // Update document title
    document.title = brandConfig.name;

    // Apply saved CSS variables from theme
    if (brandConfig.savedCssVariables) {

      // Determine current theme mode
      const isDark = document.documentElement.classList.contains('dark');
      const varsToApply = isDark ? brandConfig.savedCssVariables.dark : brandConfig.savedCssVariables.light;

      // Apply all saved CSS variables
      if (varsToApply && typeof varsToApply === 'object') {
        Object.entries(varsToApply).forEach(([property, value]) => {
          root.style.setProperty(property, value as string);
        });
      }
    }
  }, [brandConfig]);

  // Listen for theme mode changes to apply correct CSS variables
  useEffect(() => {
    if (!brandConfig.savedCssVariables) return;

    const handleThemeChange = () => {
      const root = document.documentElement;
      const isDark = document.documentElement.classList.contains('dark');
      const varsToApply = isDark ? brandConfig.savedCssVariables!.dark : brandConfig.savedCssVariables!.light;
            
      // Apply the appropriate theme variables
      Object.entries(varsToApply).forEach(([property, value]) => {
        root.style.setProperty(property, value as string);
      });
    };

    // Create a MutationObserver to watch for class changes on the html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    // Start observing
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Cleanup
    return () => observer.disconnect();
  }, [brandConfig.savedCssVariables]);

  // Function to apply theme colors in real-time (for theme editor)
  const applyThemeColors = useCallback((colors: ThemeColors) => {
    const root = document.documentElement;
    const isDark = document.documentElement.classList.contains('dark');

    // Apply the appropriate theme variables based on current theme
    const varsToApply = isDark ? colors.cssVariables.dark : colors.cssVariables.light;

    Object.entries(varsToApply).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  }, []);

  const contextValue: BrandContextType = {
    brandConfig,
    isLoading,
    error,
    refreshBrandConfig,
    applyThemeColors,
  };

  return (
    <BrandContext.Provider value={contextValue}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrandConfig(): BrandContextType {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrandConfig must be used within a BrandProvider');
  }
  return context;
}
